import { iconForName } from "@/components/artifacts/iconForName.ts";
import { EventDateLink } from "@/components/artifacts/shared/EventDateLink.tsx";
import { LocationDisplay } from "@/components/ui/location-display.tsx";
import { formatPhoneNumber } from "@/lib/phone.ts";
import { cn } from "@/lib/utils.ts";
import { isInAppPath } from "@/shared/appLink.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import type { ReactNode } from "react";
import { Link } from "wouter";

type FieldRowComponentData = Extract<ArtifactComponent, { type: "fieldRow" }>;

const FormattedValue = ({
  value,
  valueType,
}: {
  value: string;
  valueType: FieldRowComponentData["valueType"];
}) => {
  switch (valueType) {
    case "location":
      return <LocationDisplay location={value} />;
    case "phoneNumber":
      return <span className="text-sm">{formatPhoneNumber(value)}</span>;
    case "email":
      // `value` can be assistant-written and is never validated as an address,
      // so encode it: a raw `?`/`&` would add headers (bcc, body) to the draft.
      // `@` stays literal — RFC 6068 writes the addr-spec separator unencoded.
      return (
        <a
          href={`mailto:${encodeURIComponent(value).replaceAll("%40", "@")}`}
          className="link text-sm"
        >
          {value}
        </a>
      );
    case "badge":
      return <span className="rounded-full border px-2 py-0.5 text-xs font-medium">{value}</span>;
    case "url": {
      // Only linkify http(s) URLs. `value` can originate from LLM-authored
      // booking state, so a `javascript:`/`data:` scheme could execute on
      // click, and a schemeless value would navigate the SPA relatively —
      // fall back to plain text in both cases. `URL.parse` returns null for an
      // unparseable value, which likewise stays text.
      const parsed = URL.parse(value);
      if (!parsed || !/^https?:$/.test(parsed.protocol))
        return <span className="text-sm break-all">{value}</span>;
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="link text-sm break-all"
        >
          {value}
        </a>
      );
    }
    case "text":
    default:
      return <span className="text-sm">{value}</span>;
  }
};

/**
 * The value wrapped in whatever link the row asks for: the calendar day-view for
 * a dated row, an in-app route for a row naming one, and no wrapper at all
 * otherwise. One case per early return — the three are alternatives, and nesting
 * them as ternaries hid that behind the JSX.
 */
const LinkedValue = ({
  calendarLink,
  appLink,
  children,
}: {
  calendarLink: FieldRowComponentData["calendarLink"];
  appLink: string | undefined;
  children: ReactNode;
}) => {
  if (calendarLink) {
    return (
      <EventDateLink
        startDateMs={calendarLink.startDateMs}
        endDateMs={calendarLink.endDateMs}
        isAllDay={calendarLink.isAllDay}
      >
        {children}
      </EventDateLink>
    );
  }
  // Enclosing navigation boundaries choose an in-place surface for this
  // canonical app path, or let it fall through to the page.
  if (appLink) {
    return (
      <Link href={appLink} className="underline">
        {children}
      </Link>
    );
  }
  return <>{children}</>;
};

export const FieldRowBlock = ({ component }: { component: FieldRowComponentData }) => {
  const Icon = component.icon ? iconForName(component.icon) : null;
  // A value with nothing to wrap at — a URL, an id, a token — breaks mid-word
  // rather than running past the card's edge.
  const unbroken = !/\s/.test(component.value);
  const value = <FormattedValue value={component.value} valueType={component.valueType} />;
  // Wouter only calls `preventDefault` on an unmodified left-click; a ctrl/cmd/
  // shift-click returns early and lets the browser act on the raw `<a href>`. So
  // a hostile `appLink` would still reach the browser that way — hence the same
  // guard `EmailLinkBlock` applies, rather than trusting the SPA intercept.
  const appLink =
    component.appLink && isInAppPath(component.appLink) ? component.appLink : undefined;
  const wrapped = (
    <LinkedValue calendarLink={component.calendarLink} appLink={appLink}>
      {value}
    </LinkedValue>
  );
  const label = component.label && (
    // `prominent` matches the recipient-row label (bold + w-20) so the
    // value lines up with adjacent participant pills.
    <span
      className={cn(
        component.prominent ? "w-20 shrink-0 font-medium" : "text-muted-foreground shrink-0",
      )}
    >
      {component.label}
    </span>
  );
  // Shown whole, however long it runs: this is a card people approve from, and
  // the argument is the thing they are approving. `min-w-0` lets the value wrap
  // at the row's width instead of setting it — a flex item sized by its content
  // pushes the row past the card and is clipped by the panel, padding and all.
  // The server already caps what it sends, so the height is bounded without the
  // card hiding any of what it was given.
  const body = (
    <div
      className={cn(
        "min-w-0 whitespace-pre-wrap",
        unbroken ? "break-all" : "break-words",
        // Justified only where there are spaces to stretch: a URL broken
        // mid-path has none, and justifying it would open rivers between the
        // fragments instead of squaring the block off.
        !unbroken && "text-justify",
      )}
    >
      {wrapped}
    </div>
  );

  // A row with a label is a name and the thing it names, and the two belong on
  // lines of their own: the label alone, the value under it with the card's
  // whole width to wrap into.
  //
  // Two kinds of row stay on one line. A `prominent` row is a header line — the
  // tool a call runs, the server it runs on — whose label reads as part of that
  // line. And a row with no label leads with an icon that *is* its label: an
  // event's calendar or map pin, which says what the value is only while it sits
  // beside it.
  if (component.label && !component.prominent) {
    return (
      // `py-1` on top of the view's own gap, so consecutive values read as
      // separate rows rather than one block of text — and wider than the
      // `gap-0.5` holding each label to its own value.
      <div className="flex flex-col gap-0.5 text-sm min-w-0 py-1">
        <div className="flex flex-row gap-2 items-center">
          {Icon ? <Icon className="size-4 text-muted-foreground shrink-0" /> : null}
          {label}
        </div>
        {body}
      </div>
    );
  }

  return (
    // `items-start` so a label stays level with the first line of a value that
    // runs to several.
    <div className="flex flex-row gap-2 items-start text-sm min-w-0 py-1">
      {Icon ? <Icon className="size-4 text-muted-foreground shrink-0 mt-0.5" /> : null}
      {label}
      {body}
    </div>
  );
};
